import React, { useEffect, useMemo, useRef } from 'react';
import { useSimulationStore } from '../../store/useSimulationStore';
import { useCanvasStore } from '../../store/useCanvasStore';
import { calculateRadialLayout } from './RadialLayout';
import { calculateTreeLayout } from './TreeLayout';
import { ConnectorLine } from './ConnectorLine';
import { NodeCard } from './NodeCard';
import { findPathToNode } from '../../utils/treeUtils';

interface StructureCanvasProps {
  onNodeSelect?: (nodeId: string) => void;
  onAddChild?: (parentId: string) => void;
}

export const StructureCanvas: React.FC<StructureCanvasProps> = ({
  onNodeSelect,
  onAddChild,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, camX: 0, camY: 0 });


  // Store bindings
  const getCurrentRootNode = useSimulationStore((state) => state.getCurrentRootNode);
  const mode = useSimulationStore((state) => state.mode);
  const activePreset = useSimulationStore((state) => state.activePreset);
  const addSandboxNode = useSimulationStore((state) => state.addSandboxNode);

  const layoutMode = useCanvasStore((state) => state.layoutMode);
  const treeOrientation = useCanvasStore((state) => state.treeOrientation);
  const camera = useCanvasStore((state) => state.camera);
  const setCamera = useCanvasStore((state) => state.setCamera);
  const selectedNodeId = useCanvasStore((state) => state.selectedNodeId);
  const setSelectedNodeId = useCanvasStore((state) => state.setSelectedNodeId);
  const searchQuery = useCanvasStore((state) => state.searchQuery);
  const hideDormant = useCanvasStore((state) => state.hideDormant);
  const fitToScreen = useCanvasStore((state) => state.fitToScreen);
  const setContainerSize = useCanvasStore((state) => state.setContainerSize);

  const rootNode = getCurrentRootNode();

  // Compute Layout (Radial vs Tree) - Always show all consultants
  const layout = useMemo(() => {
    if (layoutMode === 'radial') {
      return calculateRadialLayout(rootNode, false);
    }
    return calculateTreeLayout(rootNode, treeOrientation, false);
  }, [rootNode, layoutMode, treeOrientation]);

  // Compute highlighted root-to-node path
  const highlightedPathSet = useMemo(() => {
    const set = new Set<string>();
    let targetId = selectedNodeId;

    if (searchQuery.trim().length > 0) {
      const match = layout.nodes.find((n) =>
        n.data.name.toLowerCase().includes(searchQuery.toLowerCase().trim())
      );
      if (match) {
        targetId = match.id;
      }
    }

    if (targetId) {
      const path = findPathToNode(rootNode, targetId);
      for (const id of path) {
        set.add(id);
      }
    }
    return set;
  }, [selectedNodeId, searchQuery, layout.nodes, rootNode]);

  // Auto-fit on layout, orientation, or preset switch
  useEffect(() => {
    if (containerRef.current) {
      const { clientWidth, clientHeight } = containerRef.current;
      if (clientWidth > 0 && clientHeight > 0) {
        fitToScreen(clientWidth, clientHeight, layout.bounds);
      }
    }
  }, [layoutMode, treeOrientation, activePreset]);

  // Dynamic ResizeObserver to auto-center and fit when viewport resizes (e.g. tablet rotation)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setContainerSize({ width, height });
          fitToScreen(width, height, layout.bounds);
        }
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [layout.bounds, fitToScreen, setContainerSize]);

  // Native non-passive wheel listener to prevent browser page zoom while zooming canvas
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onWheelNative = (e: WheelEvent) => {
      e.preventDefault();

      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const currentCam = useCanvasStore.getState().camera;
      const zoomFactor = e.deltaY < 0 ? 1.12 : 0.88;
      const newZoom = Math.max(0.15, Math.min(3.0, currentCam.zoom * zoomFactor));

      const worldX = (mouseX - currentCam.x) / currentCam.zoom;
      const worldY = (mouseY - currentCam.y) / currentCam.zoom;

      const newX = mouseX - worldX * newZoom;
      const newY = mouseY - worldY * newZoom;

      useCanvasStore.getState().setCamera({ x: newX, y: newY, zoom: newZoom });
    };

    container.addEventListener('wheel', onWheelNative, { passive: false });
    return () => {
      container.removeEventListener('wheel', onWheelNative);
    };
  }, []);

  // Mouse drag pan
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Left click only
    isDraggingRef.current = true;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      camX: camera.x,
      camY: camera.y,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setCamera({
      x: dragStartRef.current.camX + dx,
      y: dragStartRef.current.camY + dy,
    });
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };

  // Native non-passive touch handlers (1-finger pan, 2-finger pinch)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isDragging = false;
    let dragStart = { x: 0, y: 0, camX: 0, camY: 0 };
    let touchState: {
      initialDistance: number;
      initialZoom: number;
      initialCentroid: { x: number; y: number };
      initialCam: { x: number; y: number };
    } | null = null;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2 && e.cancelable) {
        e.preventDefault();
      }
      const currentCam = useCanvasStore.getState().camera;
      if (e.touches.length === 1) {
        isDragging = true;
        dragStart = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          camX: currentCam.x,
          camY: currentCam.y,
        };
        touchState = null;
      } else if (e.touches.length === 2) {
        isDragging = false;
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const distance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        const rect = container.getBoundingClientRect();
        const centroid = {
          x: (t1.clientX + t2.clientX) / 2 - rect.left,
          y: (t1.clientY + t2.clientY) / 2 - rect.top,
        };

        touchState = {
          initialDistance: distance,
          initialZoom: currentCam.zoom,
          initialCentroid: centroid,
          initialCam: { x: currentCam.x, y: currentCam.y },
        };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.cancelable) {
        e.preventDefault();
      }

      if (e.touches.length === 1 && isDragging) {
        const dx = e.touches[0].clientX - dragStart.x;
        const dy = e.touches[0].clientY - dragStart.y;
        useCanvasStore.getState().setCamera({
          x: dragStart.camX + dx,
          y: dragStart.camY + dy,
        });
      } else if (e.touches.length === 2 && touchState) {
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const distance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        const rect = container.getBoundingClientRect();
        const currentCentroid = {
          x: (t1.clientX + t2.clientX) / 2 - rect.left,
          y: (t1.clientY + t2.clientY) / 2 - rect.top,
        };

        const scaleRatio = distance / touchState.initialDistance;
        const newZoom = Math.max(0.15, Math.min(3.0, touchState.initialZoom * scaleRatio));

        const worldX =
          (touchState.initialCentroid.x - touchState.initialCam.x) / touchState.initialZoom;
        const worldY =
          (touchState.initialCentroid.y - touchState.initialCam.y) / touchState.initialZoom;

        const newX = currentCentroid.x - worldX * newZoom;
        const newY = currentCentroid.y - worldY * newZoom;

        useCanvasStore.getState().setCamera({ x: newX, y: newY, zoom: newZoom });
      }
    };

    const onTouchEnd = () => {
      isDragging = false;
      touchState = null;
    };

    const onGesture = (e: Event) => {
      e.preventDefault();
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: false });
    container.addEventListener('touchcancel', onTouchEnd, { passive: false });
    container.addEventListener('gesturestart', onGesture, { passive: false });
    container.addEventListener('gesturechange', onGesture, { passive: false });
    container.addEventListener('gestureend', onGesture, { passive: false });

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
      container.removeEventListener('gesturestart', onGesture);
      container.removeEventListener('gesturechange', onGesture);
      container.removeEventListener('gestureend', onGesture);
    };
  }, []);

  const handleNodeClick = (nodeId: string) => {
    setSelectedNodeId(nodeId === selectedNodeId ? null : nodeId);
    onNodeSelect?.(nodeId);
  };

  const handleAddChildAction = (parentId: string) => {
    if (onAddChild) {
      onAddChild(parentId);
    } else if (mode === 'sandbox') {
      addSandboxNode(parentId);
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'svg') {
      setSelectedNodeId(null);
    }
  };

  const hasHighlightedSelection = highlightedPathSet.size > 0;

  // Memoize sorted edges to guarantee highlighted path lines render on top
  // while avoiding re-sorting on every single pan/zoom frame
  const sortedEdges = useMemo(() => {
    if (!hasHighlightedSelection) {
      return layout.edges;
    }
    return [...layout.edges].sort((a, b) => {
      const aHigh =
        highlightedPathSet.has(a.sourceId) && highlightedPathSet.has(a.targetId) ? 1 : 0;
      const bHigh =
        highlightedPathSet.has(b.sourceId) && highlightedPathSet.has(b.targetId) ? 1 : 0;
      return aHigh - bHigh;
    });
  }, [layout.edges, hasHighlightedSelection, highlightedPathSet]);

  return (
    <div
      ref={containerRef}
      data-canvas-container="true"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onClick={handleBackgroundClick}
      className="w-full h-full relative overflow-hidden bg-[#0A0F1D] select-none cursor-grab active:cursor-grabbing touch-none"
    >
      <svg className="w-full h-full block">
        <defs>
          {/* Neon Glow Filters - Expanded bounds to prevent clipping */}
          <filter id="neon-glow-emerald" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="neon-glow-cyan" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="4.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Linear Gradient for Connections */}
          <linearGradient id="edge-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.85" />
          </linearGradient>
        </defs>

        {/* Transformed Canvas Content Layer */}
        <g transform={`translate(${camera.x}, ${camera.y}) scale(${camera.zoom})`}>
          {/* LAYER 1: Connector Lines */}
          <g className="edges-layer">
            {sortedEdges.map((edge) => {
              const isPathHighlighted =
                hasHighlightedSelection &&
                highlightedPathSet.has(edge.sourceId) &&
                highlightedPathSet.has(edge.targetId);

              return (
                <ConnectorLine
                  key={`${edge.sourceId}-${edge.targetId}`}
                  edge={edge}
                  layoutMode={layoutMode}
                  treeOrientation={treeOrientation}
                  isHighlighted={isPathHighlighted}
                  isDormant={hideDormant}
                  showBadge={camera.zoom >= 0.45}
                />
              );
            })}
          </g>

          {/* LAYER 2: Node Cards */}
          <g className="nodes-layer">
            {layout.nodes.map((node) => {
              const isSelected = selectedNodeId === node.id;
              const isHighlighted = highlightedPathSet.has(node.id);
              const isDimmed = hasHighlightedSelection && !isHighlighted;

              return (
                <NodeCard
                  key={node.id}
                  node={node}
                  isSelected={isSelected}
                  isHighlighted={isHighlighted}
                  isDimmed={isDimmed}
                  onClick={handleNodeClick}
                  onAddChild={handleAddChildAction}
                />
              );
            })}
          </g>
        </g>
      </svg>
    </div>
  );
};
